export const INVOICE_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "awaiting_approval", label: "Awaiting Approval" },
  { value: "approved", label: "Approved" },
  { value: "awaiting_payment", label: "Awaiting Payment" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
  { value: "cancelled", label: "Cancelled" },
  { value: "void", label: "Void" },
  { value: "refunded", label: "Refunded" },
];

export const DEFAULT_INVOICE_STATUS = "draft";

export const INVOICE_CREATE_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "awaiting_payment", label: "Awaiting Payment" },
  { value: "awaiting_approval", label: "Awaiting Approval" },
];

const CREATE_STATUS_VALUES = new Set(
  INVOICE_CREATE_STATUSES.map((item) => item.value),
);

const STATUS_ALIASES = {
  draft: "draft",
  pending: "draft",
  quotation: "draft",
  accepted: "draft",
  awaiting_payment: "awaiting_payment",
  "awaiting payment": "awaiting_payment",
  awaiting: "awaiting_payment",
  issued: "awaiting_payment",
  unpaid: "awaiting_payment",
  awaiting_approval: "awaiting_approval",
  "awaiting approval": "awaiting_approval",
  approval: "awaiting_approval",
  pending_approval: "awaiting_approval",
  "pending approval": "awaiting_approval",
  approved: "approved",
  approve: "approved",
  approve_invoice: "approved",
  partial: "partial",
  partially_paid: "partial",
  "partially paid": "partial",
  paid: "paid",
  completed: "paid",
  overdue: "overdue",
  cancelled: "cancelled",
  canceled: "cancelled",
  void: "void",
  voided: "void",
  refunded: "refunded",
  credit_issued: "refunded",
  "credit issued": "refunded",
};

const STATUS_PDF_STYLES = {
  draft: { background: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
  awaiting_approval: { background: "#f3e8ff", color: "#7c3aed", border: "#d8b4fe" },
  approved: { background: "#e0f2fe", color: "#0369a1", border: "#7dd3fc" },
  awaiting_payment: { background: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
  partial: { background: "#fef3c7", color: "#b45309", border: "#fcd34d" },
  paid: { background: "#dcfce7", color: "#166534", border: "#86efac" },
  overdue: { background: "#fee2e2", color: "#b91c1c", border: "#fca5a5" },
  cancelled: { background: "#e2e8f0", color: "#475569", border: "#94a3b8" },
  void: { background: "#f8fafc", color: "#64748b", border: "#cbd5e1" },
  refunded: { background: "#ede9fe", color: "#6d28d9", border: "#c4b5fd" },
};

export function normalizeInvoiceStatus(value) {
  const raw = String(value || "")
    .toLowerCase()
    .trim()
    .replace(/-/g, "_");

  const aliased = STATUS_ALIASES[raw] || STATUS_ALIASES[raw.replace(/_/g, " ")] || raw;
  return INVOICE_STATUSES.some((item) => item.value === aliased)
    ? aliased
    : DEFAULT_INVOICE_STATUS;
}

export function getInvoiceStatusLabel(value) {
  const normalized = normalizeInvoiceStatus(value);
  return (
    INVOICE_STATUSES.find((item) => item.value === normalized)?.label || "Draft"
  );
}

export function getInvoiceStatusBadgeClass(value) {
  return `invoice-hub-status invoice-hub-status--${normalizeInvoiceStatus(value)}`;
}

export function getInvoiceStatusPdfStyle(value) {
  const normalized = normalizeInvoiceStatus(value);
  return STATUS_PDF_STYLES[normalized] || STATUS_PDF_STYLES.draft;
}

export function deriveInvoiceStatusFromPayment({
  dueAmount = 0,
  paidAmount = 0,
  existingStatus = "",
  dueDate = null,
} = {}) {
  const normalizedExisting = normalizeInvoiceStatus(existingStatus);
  if (
    ["cancelled", "void", "refunded", "draft", "awaiting_approval", "approved"].includes(
      normalizedExisting,
    )
  ) {
    return normalizedExisting;
  }

  const due = Number(dueAmount) || 0;
  const paid = Number(paidAmount) || 0;

  if (due <= 0 && paid > 0) return "paid";
  if (paid > 0 && due > 0) return "partial";

  if (dueDate) {
    const dueTime = new Date(dueDate).getTime();
    if (!Number.isNaN(dueTime) && dueTime < Date.now() && due > 0) {
      return "overdue";
    }
  }

  if (due > 0) return "awaiting_payment";
  return "paid";
}

export function isValidInvoiceStatus(value) {
  return INVOICE_STATUSES.some(
    (item) => item.value === normalizeInvoiceStatus(value),
  );
}

export function isInvoiceCreateStatus(value) {
  return CREATE_STATUS_VALUES.has(normalizeInvoiceStatus(value));
}

export function resolveInvoiceCreateStatus(value) {
  const normalized = normalizeInvoiceStatus(value);
  return isValidInvoiceStatus(normalized) ? normalized : DEFAULT_INVOICE_STATUS;
}

/** Statuses where edit + add-payment workflow is allowed. */
export const INVOICE_EDITABLE_WORKFLOW_STATUSES = [
  "draft",
  "awaiting_approval",
  "approved",
  "awaiting_payment",
  "partial",
  "overdue",
];

export function isInvoiceEditableWorkflowStatus(value) {
  return INVOICE_EDITABLE_WORKFLOW_STATUSES.includes(
    normalizeInvoiceStatus(value),
  );
}

/**
 * Approve action from awaiting_approval:
 * - payment recorded → paid / partial from remaining
 * - no payment → awaiting_payment (approved flag set separately)
 */
export function resolveAwaitingApprovalActionStatus({
  totalAmount = 0,
  paidAmount = 0,
} = {}) {
  const paid = Math.max(Number(paidAmount) || 0, 0);
  if (paid <= 0) return "awaiting_payment";

  const total = Math.max(Number(totalAmount) || 0, 0);
  const remaining = Math.max(total - paid, 0);
  if (remaining <= 0.0001) return "paid";
  return "partial";
}

/**
 * Derive invoice status from amount due, discount, and payments.
 *
 * - Full discount (nothing left to collect) → paid
 * - Cash payment covering total → paid / partial
 * - Otherwise → user's normal status (draft / awaiting_payment / …)
 *
 * Important: auto-paid from discount must NOT stick. If the user clears the
 * discount or changes lines so amount is due again, fall back to baseStatus
 * (never keep a previous auto "paid").
 */
export function resolveCreateInvoiceStatusFromPayment({
  requestedStatus = DEFAULT_INVOICE_STATUS,
  totalAmount = 0,
  paidAmount = 0,
  discountAmount = 0,
  grossSubtotal = 0,
} = {}) {
  const requested = resolveInvoiceCreateStatus(requestedStatus);

  // Approval path is explicit — payment/discount do not override it.
  if (requested === "awaiting_approval") {
    return "awaiting_approval";
  }

  if (requested === "approved") {
    return "approved";
  }

  const paid = Math.max(Number(paidAmount) || 0, 0);
  const total = Math.max(Number(totalAmount) || 0, 0);
  const discount = Math.max(Number(discountAmount) || 0, 0);
  const gross = Math.max(Number(grossSubtotal) || 0, 0);

  // Runtime: discount covers everything we need to receive → paid.
  if (gross > 0.0001 && discount > 0.0001 && total <= 0.0001) {
    return "paid";
  }

  // Cash payments.
  if (paid > 0) {
    if (total <= 0.0001) return "paid";
    const remaining = Math.max(total - paid, 0);
    if (remaining <= 0.0001) return "paid";
    return "partial";
  }

  // Amount is due again (discount removed / lines changed).
  // Never keep a sticky auto-paid / partial without cash or full discount.
  if (requested === "paid" || requested === "partial") {
    return DEFAULT_INVOICE_STATUS;
  }

  return requested;
}
